import { gsap, ScrollTrigger, SplitText } from "@gsap";

const motion = {
	duration: 0.6,
	ease: "power3.out",
	stagger: 0.06,
	start: "clamp(top 92%)",
	introDelay: 0.1,
	introStep: 0.08,
};

const presets = {
	"fade-up": {
		from: { autoAlpha: 0, y: 24 },
		to: { autoAlpha: 1, y: 0 },
	},
	fade: {
		from: { autoAlpha: 0 },
		to: { autoAlpha: 1 },
	},
	line: {
		from: { scaleX: 0, transformOrigin: "left center" },
		to: { scaleX: 1 },
		duration: 0.9,
		ease: "power3.inOut",
	},
	stagger: {
		from: { autoAlpha: 0, y: 24 },
		to: { autoAlpha: 1, y: 0 },
		children: true,
	},
	sequence: {
		from: { autoAlpha: 0, y: 40 },
		to: { autoAlpha: 1, y: 0 },
		children: true,
		stagger: 0.18,
	},
};

const isAboveViewport = (el) => el.getBoundingClientRect().bottom < 0;

const isInViewport = (el) => el.getBoundingClientRect().top < window.innerHeight;

function revealTitle(el, delay = 0, onScroll = true) {
	SplitText.create(el, {
		type: "lines",
		linesClass: "reveal-line",
		autoSplit: true,
		onSplit(self) {
			return gsap.from(self.lines, {
				autoAlpha: 0,
				yPercent: 60,
				duration: motion.duration,
				ease: motion.ease,
				stagger: motion.stagger,
				delay,
				scrollTrigger: onScroll ? { trigger: el, start: motion.start, once: true } : undefined,
			});
		},
	});
}

function revealNow(el, delay) {
	if (el.dataset.reveal === "title") {
		revealTitle(el, delay, false);
		return;
	}

	const preset = presets[el.dataset.reveal];
	if (!preset) return;

	const targets = targetsOf(el, preset);
	gsap.set(targets, preset.from);
	gsap.to(targets, {
		...preset.to,
		duration: preset.duration ?? motion.duration,
		ease: preset.ease ?? motion.ease,
		stagger: preset.stagger ?? motion.stagger,
		delay,
		clearProps: "opacity,visibility,transform",
	});
}

function targetsOf(el, preset) {
	return preset.children ? gsap.utils.toArray(el.children) : el;
}

function revealBatch(elements) {
	elements.forEach((el) => {
		const preset = presets[el.dataset.reveal];
		gsap.set(targetsOf(el, preset), preset.from);
	});

	ScrollTrigger.batch(elements, {
		start: motion.start,
		once: true,
		onEnter(batch) {
			batch.forEach((el, index) => {
				const preset = presets[el.dataset.reveal];
				const targets = targetsOf(el, preset);

				if (isAboveViewport(el)) {
					gsap.set(targets, { clearProps: "opacity,visibility,transform" });
					return;
				}

				gsap.to(targets, {
					...preset.to,
					duration: preset.duration ?? motion.duration,
					ease: preset.ease ?? motion.ease,
					stagger: preset.stagger ?? motion.stagger,
					delay: index * motion.stagger,
					clearProps: "opacity,visibility,transform",
				});
			});
		},
	});
}

function scan(scope = document) {
	const elements = gsap.utils
		.toArray("[data-reveal]", scope)
		.filter((el) => !el.dataset.revealReady);

	elements.forEach((el) => {
		el.dataset.revealReady = "true";
	});

	const above = scope === document ? elements.filter(isAboveViewport) : [];
	const visible = scope === document ? elements.filter((el) => !above.includes(el) && isInViewport(el)) : [];
	const later = elements.filter((el) => !above.includes(el) && !visible.includes(el));

	visible.forEach((el, index) => revealNow(el, motion.introDelay + index * motion.introStep));

	later.filter((el) => el.dataset.reveal === "title").forEach((el) => revealTitle(el));

	const batched = later.filter((el) => presets[el.dataset.reveal]);
	if (batched.length) revealBatch(batched);
}

document.addEventListener("DOMContentLoaded", () => {
	const mm = gsap.matchMedia();

	mm.add("(prefers-reduced-motion: no-preference)", (context) => {
		scan();
		window.pkReveal = (scope) => context.add(() => scan(scope));

		return () => {
			document.querySelectorAll("[data-reveal-ready]").forEach((el) => {
				delete el.dataset.revealReady;
			});
			window.pkReveal = () => {};
		};
	});

	window.pkReveal ??= () => {};

	let height = document.body.scrollHeight;
	let timer;

	new ResizeObserver(() => {
		if (document.body.scrollHeight === height) return;
		height = document.body.scrollHeight;
		clearTimeout(timer);
		timer = setTimeout(() => ScrollTrigger.refresh(), 150);
	}).observe(document.body);

	document.fonts?.ready.then(() => ScrollTrigger.refresh());
});
